const REGISTRY_KEY = "ciq:tab-registry";
const HEARTBEAT_AT_KEY = "ciq:tab-heartbeat-at";
// Refresh own liveness every 10s; a tab unseen for 90s (9 missed ticks) is
// treated as dead. The wide window tolerates background-tab timer throttling
// (Chrome caps backgrounded timers near 1/min) so an open-but-backgrounded tab
// is not pruned by an active peer and still counts.
const PRESENCE_INTERVAL_MS = 10_000;
const STALE_MS = 90_000;

export interface TabRegistry {
  getTabCount(): number;
  isLeader(): boolean;
  lastHeartbeatAt(): number;
  markHeartbeat(now: number): void;
  stop(): void;
}

let idCounter = 0;

// Tab label for leader election, never a security token. randomUUID needs a
// secure context, so getRandomValues covers plain-http origins before the
// counter fallback.
const safeRandomId = (): string => {
  try {
    if (typeof crypto !== "undefined") {
      if (typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }
      if (typeof crypto.getRandomValues === "function") {
        const bytes = crypto.getRandomValues(new Uint8Array(8));
        return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
          ""
        );
      }
    }
  } catch {
    // fall through to the counter id
  }
  idCounter += 1;
  return `${Date.now()}-${idCounter}`;
};

const readRegistry = (): Record<string, number> => {
  try {
    const raw = localStorage.getItem(REGISTRY_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const writeRegistry = (entries: Record<string, number>): void => {
  try {
    localStorage.setItem(REGISTRY_KEY, JSON.stringify(entries));
  } catch {
    // private mode / quota: presence just degrades to single-tab
  }
};

const liveIds = (entries: Record<string, number>, now: number): string[] =>
  Object.keys(entries).filter((id) => now - entries[id] < STALE_MS);

export function startTabRegistry(opts?: { tabId?: string }): TabRegistry {
  const tabId = opts?.tabId ?? safeRandomId();

  const refresh = (): void => {
    const now = Date.now();
    const entries = readRegistry();
    entries[tabId] = now;
    for (const id of Object.keys(entries)) {
      if (now - entries[id] >= STALE_MS) {
        delete entries[id];
      }
    }
    writeRegistry(entries);
  };

  const removeSelf = (): void => {
    const entries = readRegistry();
    delete entries[tabId];
    writeRegistry(entries);
  };

  refresh();

  let intervalId: ReturnType<typeof setInterval> | undefined;
  try {
    intervalId = setInterval(refresh, PRESENCE_INTERVAL_MS);
  } catch {
    intervalId = undefined;
  }

  const onUnload = (): void => removeSelf();
  try {
    window.addEventListener("pagehide", onUnload);
    window.addEventListener("beforeunload", onUnload);
  } catch {
    // no window (defensive) — nothing to unbind later
  }

  return {
    getTabCount(): number {
      const count = liveIds(readRegistry(), Date.now()).length;
      return count > 0 ? count : 1;
    },
    isLeader(): boolean {
      const ids = liveIds(readRegistry(), Date.now());
      if (ids.length === 0) {
        return true;
      }
      // Lowest id wins, compared by codepoint — never localeCompare, whose
      // collation varies across browsers and could elect two leaders. The
      // membership test keeps a tab that failed to register from displacing
      // a peer that did.
      return ids.includes(tabId) && ids.every((id) => tabId <= id);
    },
    lastHeartbeatAt(): number {
      try {
        return Number(localStorage.getItem(HEARTBEAT_AT_KEY)) || 0;
      } catch {
        return 0;
      }
    },
    markHeartbeat(now: number): void {
      try {
        localStorage.setItem(HEARTBEAT_AT_KEY, String(now));
      } catch {
        // ignore
      }
    },
    stop(): void {
      if (intervalId !== undefined) {
        clearInterval(intervalId);
      }
      try {
        window.removeEventListener("pagehide", onUnload);
        window.removeEventListener("beforeunload", onUnload);
      } catch {
        // ignore
      }
      removeSelf();
    }
  };
}
